import type {
  RootSubject,
  TermInterface,
  WeekInterface,
  SubjectInterface,
  GradeSubjectMetaDataInterface
} from "@ts/interfaces/subjects/api/subjectsApiInterface";
import type { Lesson } from "@ts/interfaces/terms/api/termsApiInterface";
import { SubjectType } from "@ts/enums/Subjects";
import { COMPLETION_STATUS, type CompletionStatus } from "@ts/enums/CompletionStatus";
import type {
  GradeStructureApiResponse,
  StructureTerm,
  StructureSubject,
  StructureWeek,
  StructureLesson,
  StructureSubjectMetadata
} from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type {
  GradeProgressApiResponse,
  ProgressTerm
} from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import { asArray } from "./mergeTerms";

// /structure serves snake_case metadata; the subjects view reads the legacy
// camelCase GradeSubjectMetaDataInterface, so map across here.
function mapMetadata(
  m: StructureSubjectMetadata
): GradeSubjectMetaDataInterface {
  return {
    altTag: m.alt_tag,
    imageUrl: "",
    thumbnailUrl: m.thumbnail_url,
    description: "",
    long_desc: m.long_desc,
    achievement_standard_summary: m.achievement_standard_summary,
    courseNames: m.course_names as GradeSubjectMetaDataInterface["courseNames"]
  };
}

function statusByUsageKey(
  progress: GradeProgressApiResponse | null
): Map<string, CompletionStatus> {
  const map = new Map<string, CompletionStatus>();
  // progress.lessons is keyed by usage_key → { status }.
  for (const [usageKey, lesson] of Object.entries(progress?.lessons ?? {})) {
    map.set(usageKey, lesson.status);
  }
  return map;
}

function mapNestedSubject(
  subject: StructureSubject,
  week: StructureWeek,
  statusMap: Map<string, CompletionStatus>
): SubjectInterface {
  const lessons: Lesson[] = asArray<StructureLesson>(week.lessons).map(
    lesson => ({
      id: lesson.number,
      key: lesson.key,
      name: lesson.name,
      description: lesson.description,
      status: statusMap.get(lesson.key) ?? COMPLETION_STATUS.NOT_STARTED
    })
  );

  return {
    name: subject.name,
    key: subject.key,
    slug: subject.slug,
    type: subject.type,
    plp_grade_code: subject.plp_grade_code,
    plp_grade_name: subject.plp_grade_name,
    grade_subject_uuid: subject.grade_subject_uuid,
    grade_subject_id: subject.portal_grade_subject_id,
    lessons
  };
}

/**
 * Rebuilds the legacy "all subjects" RootSubject[] from the split endpoints.
 * One entry per distinct subject (keyed by slug_with_plp), each carrying its
 * per-term tree with per-lesson status from progress.
 */
export function mergeSubjects(
  structure: GradeStructureApiResponse | null,
  progress: GradeProgressApiResponse | null
): RootSubject[] {
  if (!structure) return [];

  const statusMap = statusByUsageKey(progress);

  const progressByTerm = new Map<number, ProgressTerm>();
  for (const t of asArray<ProgressTerm>(progress?.terms)) {
    progressByTerm.set(t.id, t);
  }

  const bySlug = new Map<string, RootSubject>();

  for (const structureTerm of asArray<StructureTerm>(structure.terms)) {
    const progressTerm = progressByTerm.get(structureTerm.id);
    const progressCounts = progress?.subjects ?? {};

    for (const subject of asArray<StructureSubject>(structureTerm.subjects)) {
      const slug = subject.slug_with_plp;

      let root = bySlug.get(slug);
      if (!root) {
        root = {
          id: subject.portal_grade_subject_id,
          name: subject.name,
          slug,
          // Grade/term/PLP-agnostic identity: a PLP variant keeps the portal
          // subject id of the subject it was sourced from.
          subject_id: subject.portal_subject_id,
          grade_subject_id: subject.portal_grade_subject_id,
          grade_subject_uuid: subject.grade_subject_uuid,
          plp_grade_code: subject.plp_grade_code,
          plp_grade_name: subject.plp_grade_name,
          subject_type: subject.type as SubjectType,
          grade_subject_meta_data: mapMetadata(subject.grade_subject_metadata),
          total_lessons: 0,
          completed_lessons: 0,
          terms: []
        };
        bySlug.set(slug, root);
      }

      // Ordered by week id. One pass over the subject's weeks — the week object
      // is mapped directly, not re-found by id.
      const weeks: WeekInterface[] = asArray<StructureWeek>(subject.weeks)
        .slice()
        .sort((a, b) => a.id - b.id)
        .map(week => ({
          id: week.id,
          name: week.name,
          subjects: [mapNestedSubject(subject, week, statusMap)]
        }));

      const { lessons_total: termTotal = 0, lessons_completed: termCompleted = 0 } =
        progressCounts[subject.key] ?? {};

      const term: TermInterface = {
        id: structureTerm.id,
        name: structureTerm.name,
        lessons: { total: termTotal, total_completed: termCompleted },
        weeks,
        status: progressTerm?.status ?? COMPLETION_STATUS.NOT_STARTED,
        has_active_entitlement: subject.has_active_entitlement,
        // Per-term course id, required by elective course certificate requests.
        portal_course_id: subject.portal_course_id
      };

      root.terms.push(term);
      root.total_lessons = (root.total_lessons ?? 0) + termTotal;
      root.completed_lessons = (root.completed_lessons ?? 0) + termCompleted;
    }
  }

  return [...bySlug.values()];
}
